import { useEffect, useState } from "react";
import { X } from "lucide-react";

/**
 * Minimal GDPR / PECR-compliant cookie consent banner.
 *
 * Behaviour:
 *  - Reads `tcp:cookie_consent` from localStorage. If 'granted' or 'denied',
 *    the banner stays hidden.
 *  - On Accept → writes 'granted' and calls `gtag('consent','update',...)`
 *    so GA4 (Google Consent Mode v2 default = denied, set in index.html)
 *    begins storing analytics cookies.
 *  - On Decline → writes 'denied'. No analytics cookies are written.
 *  - On Close (X) → treated as Decline so the banner doesn't reappear.
 *
 * Non-intrusive: anchored bottom, dismissible, does not block content.
 */
const STORAGE_KEY = "tcp:cookie_consent";

export default function CookieConsentBanner() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        try {
            const existing = localStorage.getItem(STORAGE_KEY);
            if (existing !== "granted" && existing !== "denied") {
                setVisible(true);
            }
        } catch (_) {
            // localStorage blocked (private mode etc) — show banner each visit;
            // safer to ask than to silently track.
            setVisible(true);
        }
    }, []);

    const persist = (choice) => {
        try {
            localStorage.setItem(STORAGE_KEY, choice);
        } catch (_) { /* ignore */ }
        try {
            if (typeof window !== "undefined" && typeof window.gtag === "function") {
                window.gtag("consent", "update", {
                    analytics_storage: choice === "granted" ? "granted" : "denied",
                });
            }
        } catch (_) { /* ignore */ }
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div
            data-testid="cookie-consent-banner"
            role="dialog"
            aria-live="polite"
            aria-label="Cookie consent"
            className="fixed bottom-3 left-3 right-3 sm:left-4 sm:right-auto sm:bottom-4 sm:max-w-md z-[60]"
        >
            <div className="rounded-xl border border-[#243044] bg-[#111827]/95 backdrop-blur-md shadow-2xl shadow-black/40 p-4 sm:p-5">
                <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                        <p className="text-[13px] sm:text-sm text-slate-200 leading-relaxed">
                            We use cookies to understand how the library is used and to improve it.
                            Analytics cookies are only set if you accept.{" "}
                            <a
                                href="https://thecontextproject.com/policies/privacy-policy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#56CCF2] hover:underline whitespace-nowrap"
                                data-testid="cookie-consent-privacy-link"
                            >
                                Privacy policy
                            </a>
                            .
                        </p>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                onClick={() => persist("granted")}
                                data-testid="cookie-consent-accept-btn"
                                className="inline-flex items-center justify-center rounded-md bg-[#2F80ED] hover:bg-[#2F80ED]/90 text-white text-xs sm:text-sm font-medium px-3.5 py-1.5 transition-colors"
                            >
                                Accept
                            </button>
                            <button
                                type="button"
                                onClick={() => persist("denied")}
                                data-testid="cookie-consent-decline-btn"
                                className="inline-flex items-center justify-center rounded-md border border-[#243044] bg-transparent hover:bg-white/5 text-slate-200 text-xs sm:text-sm font-medium px-3.5 py-1.5 transition-colors"
                            >
                                Decline
                            </button>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => persist("denied")}
                        aria-label="Dismiss cookie banner"
                        data-testid="cookie-consent-close-btn"
                        className="shrink-0 -m-1 p-1 rounded-md text-slate-400 hover:text-slate-200 hover:bg-white/5 transition-colors"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}
